import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { Check } from 'lucide-react'
import { type ComponentPropsWithoutRef, forwardRef, type ReactNode } from 'react'
import { cn } from './cn'

/**
 * Themed wrapper around Radix DropdownMenu. Wraps Trigger / Content /
 * Item / CheckboxItem / Separator / Label / Group / Sub in
 * theme-tokenised CSS classes so call sites get the Night Ops look
 * without restyling per component.
 *
 * The wrapper exposes a small fixed surface; for advanced needs (radio
 * groups, sub-menus, custom triggers) import the underlying Radix
 * primitives directly.
 *
 * @example
 *   <DropdownMenu>
 *     <DropdownMenuTrigger asChild>
 *       <Button variant="ghost" icon={MoreHorizontal} aria-label="Acciones" />
 *     </DropdownMenuTrigger>
 *     <DropdownMenuContent>
 *       <DropdownMenuItem onSelect={onEdit}>Editar</DropdownMenuItem>
 *       <DropdownMenuItem onSelect={onDuplicate}>Duplicar</DropdownMenuItem>
 *       <DropdownMenuSeparator />
 *       <DropdownMenuItem destructive onSelect={onDelete}>Eliminar</DropdownMenuItem>
 *     </DropdownMenuContent>
 *   </DropdownMenu>
 */
export const DropdownMenu = DropdownMenuPrimitive.Root
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger
export const DropdownMenuGroup = DropdownMenuPrimitive.Group
export const DropdownMenuPortal = DropdownMenuPrimitive.Portal

export const DropdownMenuContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(function DropdownMenuContent({ className, sideOffset = 6, ...rest }, ref) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn('dropdown-menu-content', className)}
        {...rest}
      />
    </DropdownMenuPrimitive.Portal>
  )
})

export type DropdownMenuItemProps = ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Item
> & {
  /** Mark as a destructive action — switches color to var(--bad). */
  destructive?: boolean
  /** Optional leading icon node (e.g. a Lucide `<Edit3 size={14} />`). */
  icon?: ReactNode
}

export const DropdownMenuItem = forwardRef<HTMLDivElement, DropdownMenuItemProps>(
  function DropdownMenuItem({ className, destructive, icon, children, ...rest }, ref) {
    return (
      <DropdownMenuPrimitive.Item
        ref={ref}
        className={cn(
          'dropdown-menu-item',
          destructive && 'dropdown-menu-item--destructive',
          className,
        )}
        {...rest}
      >
        {icon != null && <span className="dropdown-menu-item-icon">{icon}</span>}
        <span className="dropdown-menu-item-label">{children}</span>
      </DropdownMenuPrimitive.Item>
    )
  },
)

export type DropdownMenuCheckboxItemProps = ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.CheckboxItem
>

export const DropdownMenuCheckboxItem = forwardRef<
  HTMLDivElement,
  DropdownMenuCheckboxItemProps
>(function DropdownMenuCheckboxItem({ className, children, ...rest }, ref) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      className={cn('dropdown-menu-item', 'dropdown-menu-item--check', className)}
      {...rest}
    >
      <span className="dropdown-menu-item-icon" aria-hidden="true">
        <DropdownMenuPrimitive.ItemIndicator>
          <Check size={12} />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      <span className="dropdown-menu-item-label">{children}</span>
    </DropdownMenuPrimitive.CheckboxItem>
  )
})

export const DropdownMenuLabel = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(function DropdownMenuLabel({ className, ...rest }, ref) {
  return (
    <DropdownMenuPrimitive.Label
      ref={ref}
      className={cn('dropdown-menu-label', className)}
      {...rest}
    />
  )
})

export const DropdownMenuSeparator = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(function DropdownMenuSeparator({ className, ...rest }, ref) {
  return (
    <DropdownMenuPrimitive.Separator
      ref={ref}
      className={cn('dropdown-menu-separator', className)}
      {...rest}
    />
  )
})
